'use client'

/**
 * The note page's live processing stage (#14360).
 *
 * `/notes/[id]` used to learn that processing moved forward by
 * `router.refresh()`-ing the WHOLE page every ten seconds (`useBoundedRefresh`,
 * #14241), so the finished note landed up to ten seconds after it existed and
 * every poll re-rendered every server component on the page. This asks the
 * small status read (`fetchNoteProcessingStatus`) instead, and refreshes the
 * page only when the stage it answers CHANGES.
 *
 * ## The schedule
 *
 * - Nothing on mount: the server render the page just produced is fresh.
 * - Every {@link NOTE_STATUS_POLL_MS} while a stage is active, slowing to
 *   {@link NOTE_STATUS_SLOW_POLL_MS} after {@link NOTE_STATUS_BACKOFF_AFTER_MS}
 *   — a job still going after two minutes is a long one, and a few seconds'
 *   extra latency on it costs nobody anything.
 * - Paused while the tab is hidden; re-checked at once on `visibilitychange`
 *   back to visible and on window `focus`.
 * - Stopped for good at `DONE`, `FAILED` and `NONE`.
 * - A TIME ceiling, {@link NOTE_STATUS_CEILING_MS} of wall clock from the
 *   first poll, not a poll count: the backoff and the hidden-tab pause make a
 *   count mean a different duration on every tab. Past it the hook reports
 *   `timedOut` so the row can stop promising an update that is not coming.
 *   A tab that comes back from being hidden past the ceiling still checks
 *   ONCE before giving up: the reader was away, the page was not broken, and
 *   the note most likely finished meanwhile.
 * - Held while this tab is still RECORDING into the note (`recordingHere`,
 *   #15132). Nothing advances until Stop, and a meeting longer than the
 *   ceiling would otherwise end the schedule before the work it waits for
 *   has begun. When the capture stops the schedule starts afresh, ceiling
 *   and all.
 *
 * At most one request is in flight: a focus during a slow request does not
 * stack a second one, so two answers can never land out of order.
 *
 * ## The refresh
 *
 * `router.refresh()` runs inside `startTransition`, once per stage change and
 * never on a poll that saw the same stage. The transition is what the
 * reveal's `<ViewTransition>` hooks into. The router is read through a ref:
 * its identity is not a reason to restart the schedule.
 *
 * ## The server stays authoritative
 *
 * When the page re-renders with a newer `initialStage` (the refresh landing,
 * or any other refresh), that stage is adopted without a second refresh.
 *
 * A poll answering `DONE` or `NONE` refreshes and stops, but does NOT adopt
 * that stage itself: the row renders nothing for either, so adopting it
 * early would drop the row before the finished content it gives way to has
 * arrived. The last active stage holds until the refreshed render carries
 * the finished one — for at most {@link NOTE_STATUS_FINISH_GRACE_MS}, after
 * which the hook adopts it anyway, so a failed or stale refresh cannot strand
 * the row on a step that is over. `FAILED` is adopted at once — the row is
 * where the failure is said.
 *
 * ## The stage facts (#14364)
 *
 * Beside the stage, the hook carries the facts the status read measures:
 * `stageChangedAt` (when the recording last moved — what the card's elapsed
 * clock counts from), `hasTranscript` (whether "Transcript ready" has
 * anything to link to) and `lateAudioPending` (#14423: the SUMMARIZING stage
 * is a re-run over audio that arrived after Stop). They travel with the
 * stage: from the server render, from each poll, and from a newer server
 * render.
 */

import { useRouter } from 'next/navigation'
import { startTransition, useEffect, useRef, useState } from 'react'

import type { MeetingTranscriptionStatus } from '@/lib/meetings/transcription-status'
import {
  isNoteProcessingActive,
  type NoteProcessingStage,
  type NoteProcessingStatus,
} from '@/lib/notes/note-processing'
import { fetchNoteProcessingStatus } from '@/lib/notes/note-processing-status-client'

/** Between polls while a stage is active and the job is young. */
export const NOTE_STATUS_POLL_MS = 2500
/** Between polls once the job has been going for two minutes. */
export const NOTE_STATUS_SLOW_POLL_MS = 10_000
/** When the poll slows down. */
export const NOTE_STATUS_BACKOFF_AFTER_MS = 2 * 60_000
/** When the poll gives up and says so. */
export const NOTE_STATUS_CEILING_MS = 15 * 60_000
/** How long a finished stage waits for the refreshed render to carry it. */
export const NOTE_STATUS_FINISH_GRACE_MS = 10_000

/** The wait before the next poll, `elapsedMs` into polling. Pure. */
export function noteStatusPollDelay(elapsedMs: number): number {
  return elapsedMs < NOTE_STATUS_BACKOFF_AFTER_MS
    ? NOTE_STATUS_POLL_MS
    : NOTE_STATUS_SLOW_POLL_MS
}

/** The measured facts that travel with the stage (#14364). */
export type NoteProcessingFacts = Pick<
  NoteProcessingStatus,
  'stageChangedAt' | 'hasTranscript'
> & { lateAudioPending?: boolean }

/** A render that did not pass facts: nothing is known yet. */
const NO_FACTS: NoteProcessingFacts = {
  stageChangedAt: null,
  hasTranscript: false,
}

export type NoteProcessingLiveStatus = Required<NoteProcessingFacts> & {
  stage: NoteProcessingStage
  transcription: MeetingTranscriptionStatus | null
  /** The time ceiling passed with the stage still active. */
  timedOut: boolean
}

type Snapshot = Required<NoteProcessingFacts> & {
  stage: NoteProcessingStage
  transcription: MeetingTranscriptionStatus | null
}

function snapshotOf(status: NoteProcessingStatus): Snapshot {
  return {
    stage: status.stage,
    transcription: status.transcription,
    stageChangedAt: status.stageChangedAt,
    hasTranscript: status.hasTranscript,
    lateAudioPending: status.lateAudioPending === true,
  }
}

export function useNoteProcessingStatus(
  noteId: string,
  initialStage: NoteProcessingStage,
  initialTranscription: MeetingTranscriptionStatus | null,
  initialFacts: NoteProcessingFacts = NO_FACTS,
  recordingHere = false,
  // The server render says a capture is live somewhere else (#15224). It
  // holds the note at UPLOADING for the whole meeting, so the poll runs at
  // its slow rate with no ceiling: Stop must still reach every reader.
  liveCapture = false,
): NoteProcessingLiveStatus {
  const router = useRouter()
  const routerRef = useRef(router)
  useEffect(() => {
    routerRef.current = router
  }, [router])

  const { stageChangedAt: initialChangedAt, hasTranscript: initialHas } =
    initialFacts
  const initialLate = initialFacts.lateAudioPending === true
  const [live, setLive] = useState<Snapshot>({
    stage: initialStage,
    transcription: initialTranscription,
    stageChangedAt: initialChangedAt,
    hasTranscript: initialHas,
    lateAudioPending: initialLate,
  })
  const [timedOut, setTimedOut] = useState(false)
  // A poll saw DONE / NONE and refreshed; the page's render will carry it.
  const [pendingFinish, setPendingFinish] = useState<Snapshot | null>(null)
  const awaitingServer = pendingFinish !== null

  // Adopt a newer server render — the "previous props in state" pattern, so
  // the adoption happens during render rather than one effect later.
  const [fromServer, setFromServer] = useState<Snapshot>(live)
  if (
    fromServer.stage !== initialStage ||
    fromServer.transcription !== initialTranscription ||
    fromServer.stageChangedAt !== initialChangedAt ||
    fromServer.hasTranscript !== initialHas ||
    fromServer.lateAudioPending !== initialLate
  ) {
    const next = {
      stage: initialStage,
      transcription: initialTranscription,
      stageChangedAt: initialChangedAt,
      hasTranscript: initialHas,
      lateAudioPending: initialLate,
    }
    setFromServer(next)
    setLive(next)
    setPendingFinish(null)
  }

  // The stage the PAGE was last refreshed for (or rendered with) — what a
  // poll's answer is compared against. A ref: the poll loop reads it and
  // must not restart when it changes.
  const knownStageRef = useRef(live.stage)
  useEffect(() => {
    knownStageRef.current = live.stage
  }, [live.stage])

  const active = isNoteProcessingActive(live.stage)

  useEffect(() => {
    if (!active || timedOut || awaitingServer || recordingHere) return

    let cancelled = false
    let inFlight = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const controller = new AbortController()
    const startedAt = Date.now()

    const clearTimer = () => {
      if (timer !== null) clearTimeout(timer)
      timer = null
    }

    const schedule = () => {
      clearTimer()
      if (cancelled) return
      // Paused: the visibility listener resumes it.
      if (document.visibilityState === 'hidden') return
      const elapsed = Date.now() - startedAt
      if (liveCapture) {
        timer = setTimeout(() => void check(), NOTE_STATUS_SLOW_POLL_MS)
        return
      }
      if (elapsed >= NOTE_STATUS_CEILING_MS) {
        setTimedOut(true)
        return
      }
      timer = setTimeout(
        () => void check(),
        Math.min(
          noteStatusPollDelay(elapsed),
          NOTE_STATUS_CEILING_MS - elapsed,
        ),
      )
    }

    const check = async () => {
      clearTimer()
      if (cancelled || inFlight) return
      // A timer armed before the tab was hidden must not poll a hidden tab.
      if (document.visibilityState === 'hidden') return
      // No ceiling check here: `schedule` enforces it after this answer, so
      // a tab woken past the ceiling looks once before it gives up.
      inFlight = true
      const status = await fetchNoteProcessingStatus(noteId, {
        signal: controller.signal,
      })
      inFlight = false
      if (cancelled) return

      if (status !== null) {
        if (status.stage !== knownStageRef.current) {
          knownStageRef.current = status.stage
          startTransition(() => routerRef.current.refresh())
        }
        if (status.stage === 'DONE' || status.stage === 'NONE') {
          // Hold the last active stage until the refreshed render lands.
          setPendingFinish(snapshotOf(status))
          return
        }
        setLive(snapshotOf(status))
        // Settled: the state change tears this effect down.
        if (!isNoteProcessingActive(status.stage)) return
      }
      schedule()
    }

    const wake = () => {
      if (document.visibilityState !== 'visible') return
      void check()
    }

    document.addEventListener('visibilitychange', wake)
    window.addEventListener('focus', wake)
    schedule()

    return () => {
      cancelled = true
      clearTimer()
      controller.abort()
      document.removeEventListener('visibilitychange', wake)
      window.removeEventListener('focus', wake)
    }
  }, [noteId, active, timedOut, awaitingServer, recordingHere, liveCapture])

  // The refreshed render did not carry the finished stage in time: adopt it.
  useEffect(() => {
    if (pendingFinish === null) return
    const timer = setTimeout(() => {
      setLive(pendingFinish)
      setPendingFinish(null)
    }, NOTE_STATUS_FINISH_GRACE_MS)
    return () => clearTimeout(timer)
  }, [pendingFinish])

  return { ...live, timedOut }
}
